/* The MCP servers' queries and writes, and the OAuth sign-in handshake.

   Writes invalidate the whole `mcp` prefix (rule book §3 rule 5): one server can
   be mounted on several agents, so a narrower key would leave another agent's
   list stale. The dialog renders its own errors, so its mutations are `silent`. */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  OAUTH_CALLBACK_PATH,
  OAUTH_CHANNEL,
  completeOAuth,
  createServer,
  deleteServer,
  getServer,
  linkServer,
  listServers,
  mcpKeys,
  patchServer,
  refreshServer,
  setLinkEnabled,
  startOAuth,
  testServer,
  unlinkServer,
} from "../api/mcp";

import type { McpOAuthReturn, McpOAuthStart, McpServer, McpServerPatch } from "@/shared/types";

/** With `agentId`, the servers mounted on it; without, the whole workspace's. */
export function useMcpServers(agentId?: string) {
  return useQuery({
    queryKey: mcpKeys.servers(agentId),
    queryFn: () => listServers(agentId),
  });
}

export function useMcpServer(id: string | null) {
  return useQuery({ queryKey: mcpKeys.server(id ?? ""), queryFn: () => getServer(id as string), enabled: !!id });
}

function useMcpWrite<A, R>(mutationFn: (args: A) => Promise<R>, silent = true) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: mcpKeys.all }),
    meta: { silent },
  });
}

export const useTestMcpServer = () => useMutation({ mutationFn: testServer, meta: { silent: true } });
export const useCreateMcpServer = () => useMcpWrite(createServer);
export const usePatchMcpServer = () =>
  useMcpWrite(({ id, body }: { id: string; body: McpServerPatch }) => patchServer(id, body));
export const useDeleteMcpServer = () => useMcpWrite(deleteServer);
export const useLinkMcpServer = () =>
  useMcpWrite(({ id, agentId }: { id: string; agentId: string }) => linkServer(id, agentId), false);
// the section renders no error of its own for these three: a failure toasts
export const useRefreshMcpServer = () => useMcpWrite(refreshServer, false);
export const useSetMcpLinkEnabled = () =>
  useMcpWrite(
    ({ id, agentId, enabled }: { id: string; agentId: string; enabled: boolean }) => setLinkEnabled(id, agentId, enabled),
    false,
  );
export const useUnlinkMcpServer = () =>
  useMcpWrite(({ id, agentId }: { id: string; agentId: string }) => unlinkServer(id, agentId), false);

export type SignInPhase = "idle" | "starting" | "waiting" | "blocked" | "finishing";

/** Sign in to an MCP server in a popup. `start` must run inside the click that
    asked for it: the popup is opened there, before any await, so no browser
    blocks it. The popup lands on the callback page, which posts the code over a
    BroadcastChannel; only the state this sign-in asked for is taken. */
export function useMcpSignIn(onSignedIn: (server: McpServer) => void) {
  const qc = useQueryClient();
  const [waiting, setWaiting] = useState<"waiting" | "blocked" | null>(null);
  const [refused, setRefused] = useState<Error | null>(null);
  const [authorizationUrl, setAuthorizationUrl] = useState<string | null>(null);
  const expected = useRef<string | null>(null);
  const done = useRef(onSignedIn);
  done.current = onSignedIn;

  const begin = useMutation({ mutationFn: startOAuth, meta: { silent: true } });
  const finish = useMutation({
    mutationFn: ({ state, code }: { state: string; code: string }) => completeOAuth(state, code),
    onSuccess: async (server) => {
      await qc.invalidateQueries({ queryKey: mcpKeys.all });
      done.current(server);
    },
    meta: { silent: true },
  });
  const finishWith = useRef(finish.mutate);
  finishWith.current = finish.mutate;

  useEffect(() => {
    const channel = new BroadcastChannel(OAUTH_CHANNEL);
    channel.onmessage = ({ data }: MessageEvent<McpOAuthReturn>) => {
      if (!expected.current || data.state !== expected.current) return; // another sign-in's
      expected.current = null;
      setWaiting(null);
      if (data.error || !data.code) {
        setRefused(new Error(data.error ? `The sign-in was refused: ${data.error}` : "The sign-in returned no code."));
        return;
      }
      finishWith.current({ state: data.state, code: data.code });
    };
    return () => channel.close();
  }, []);

  const start = (body: Omit<McpOAuthStart, "redirect_uri">) => {
    setRefused(null);
    finish.reset();
    const popup = window.open("about:blank", "mcp-oauth", "popup,width=520,height=720");
    begin.mutate(
      { ...body, redirect_uri: window.location.origin + OAUTH_CALLBACK_PATH },
      {
        onSuccess: (started) => {
          expected.current = new URL(started.authorization_url).searchParams.get("state");
          setAuthorizationUrl(started.authorization_url);
          if (popup) popup.location.href = started.authorization_url;
          setWaiting(popup ? "waiting" : "blocked"); // blocked: the dialog offers the link
        },
        onError: () => popup?.close(),
      },
    );
  };

  const cancel = () => {
    expected.current = null;
    setWaiting(null);
  };

  const phase: SignInPhase = begin.isPending ? "starting" : finish.isPending ? "finishing" : waiting ?? "idle";
  return { phase, error: refused ?? begin.error ?? finish.error, authorizationUrl, start, cancel };
}
